'use client'

import { useDialogPortalContainer } from '@/components/ui/dialog/DialogPortalContext'
import { ErrorDialog } from '@/components/ui/dialog/ErrorDialog'
import { Portal } from '@/components/utils/Portal'
import { useDocumentEvent } from '@/hooks/browser/useDocumentEvent'
import { useWindowEvent } from '@/hooks/browser/useWindowEvent'
import { useTimeout } from '@/hooks/useTimeout'
import { dayjs } from '@/lib/dayjs'
import { useRouter } from 'next/navigation'
import { useLockScreen } from '@/components/layout/global/hooks/useLockScreen'
import {
  createContext,
  Dispatch,
  SetStateAction,
  useContext,
  useState,
  useCallback,
  useEffect,
  ReactNode,
} from 'react'
import { useFeatureToggle } from '@/components/utils/FeatureToggleContext'
import { usePermissionValidation } from '@/components/layout/dashboard/hooks/usePermission'
import { Permission } from '@/lib/permissions'

const InactivityTimeoutContext = createContext<number | null>(null)

const InactivityTimeoutSetTimeoutContext = createContext<Dispatch<SetStateAction<number | null>>>(
  () => {}
)

export function useInactivityTimeout() {
  return useContext(InactivityTimeoutContext)
}

export function useInactivityTimeoutSetTimeout() {
  return useContext(InactivityTimeoutSetTimeoutContext)
}

export type InactivityTimeoutProps = {
  disabled?: boolean
}

export function InactivityTimeout({ disabled }: InactivityTimeoutProps) {
  const router = useRouter()
  const container = useDialogPortalContainer()
  const [warningDialogOpen, setWarningDialogOpen] = useState(false)
  const [allowTimeout, setAllowTimeout] = useState(false)
  const [clockText, setClockText] = useState('')
  const inactivityTimeout = useInactivityTimeout()
  const isTraining = useFeatureToggle('TRAINING')
  const hasFullViewPermission = usePermissionValidation(Permission.FULL_VIEW)

  const getInitialTimeoutValue = () => {
    if (inactivityTimeout !== undefined && inactivityTimeout !== null) {
      return inactivityTimeout
    }

    if (isTraining) {
      return 3_600_000 // 1 hour
    }

    if (!hasFullViewPermission) {
      return 28_800_000 // 8 hours
    }

    return 900_000 // 15 minutes
  }

  const initialTimeoutValue = getInitialTimeoutValue()
  const [remainingTime, timeout, resetTimeout] = useTimeout(initialTimeoutValue)

  const [, setLocked] = useLockScreen()

  const restart = useCallback(() => {
    if (allowTimeout) {
      resetTimeout()
    }
  }, [allowTimeout, resetTimeout])

  useDocumentEvent('mouseup', restart)
  useDocumentEvent('keyup', restart)
  useWindowEvent('scan', restart)

  useEffect(() => {
    if (!disabled) {
      const remainingTimeInSeconds = Math.round(dayjs.duration(remainingTime).asSeconds())
      const minutes = Math.floor(remainingTimeInSeconds / 60)
      const seconds = remainingTimeInSeconds % 60

      if (minutes >= 0 && seconds >= 0) {
        setClockText(`${minutes}:${seconds.toString().padStart(2, '0')}`)
      } else {
        setClockText('0:00')
      }
    }
  }, [disabled, remainingTime])

  useEffect(() => {
    if (!disabled) {
      if (timeout) {
        setLocked(false)
        router.push('/sign-out?callback=/home?expired=true')
      } else if (remainingTime <= 180_000) {
        setAllowTimeout(false)
        setWarningDialogOpen(true)
      } else {
        setAllowTimeout(true)
        setWarningDialogOpen(false)
      }
    }
  }, [disabled, timeout, remainingTime, router, resetTimeout, setLocked])

  return (
    <Portal container={container}>
      {!disabled && (
        <ErrorDialog
          open={warningDialogOpen}
          title={<span className="text-[#CD1D32] dark:text-[#FF6669]">SESSION TIMEOUT</span>}
          details={
            <span>
              ACE will timeout in <b>{clockText}</b>, and you
              <br />
              will be signed out. Continue
              <br />
              working?
            </span>
          }
          onClose={() => {
            setAllowTimeout(true)
            setTimeout(() => {
              resetTimeout()
            }, 100)
          }}
        />
      )}
    </Portal>
  )
}

export type InactivityTimeoutProviderProps = {
  disabled: boolean
  children?: ReactNode
}
export function InactivityTimoutProvider({ disabled, children }: InactivityTimeoutProviderProps) {
  const [timeout, setTimeout] = useState<number | null>(null)
  return (
    <InactivityTimeoutContext.Provider value={timeout}>
      <InactivityTimeoutSetTimeoutContext.Provider value={setTimeout}>
        <InactivityTimeout disabled={disabled} />
        {children}
      </InactivityTimeoutSetTimeoutContext.Provider>
    </InactivityTimeoutContext.Provider>
  )
}
